import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { X, Users, Calendar, Clock, ChevronRight, Check, AlertCircle } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PlantaoModal = ({ isOpen, onClose, currentUser }) => {
  const [corretores, setCorretores] = useState([]);
  const [selectedCorretores, setSelectedCorretores] = useState([]);
  const [corretorPlantao, setCorretorPlantao] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [plantaoAtual, setPlantaoAtual] = useState(null);
  
  const token = localStorage.getItem('token');
  const hoje = new Date();
  const diaSemana = hoje.getDay(); // 0 = domingo, 5 = sexta, 6 = sábado
  const isSextaOuFds = diaSemana >= 5 || diaSemana === 0;

  useEffect(() => {
    if (isOpen) {
      fetchData();
    }
  }, [isOpen]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [corretoresRes, plantaoRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/users`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${BACKEND_URL}/api/plantao/hoje`, {
          headers: { Authorization: `Bearer ${token}` }
        }).catch(() => ({ data: null }))
      ]);

      // Filtrar corretores ativos: role='corretor' OU role='diretor_comercial' OU tambem_corretor=true
      // Excluir: em_ferias=true OU aviso_previo=true
      const corretoresAtivos = corretoresRes.data.filter(
        u => (u.role === 'corretor' || u.role === 'diretor_comercial' || u.tambem_corretor === true) && 
             !u.em_ferias && 
             !u.aviso_previo
      );
      setCorretores(corretoresAtivos);

      if (plantaoRes.data) {
        setPlantaoAtual(plantaoRes.data);
        setSelectedCorretores(plantaoRes.data.corretores_roleta || []);
        setCorretorPlantao(plantaoRes.data.corretor_plantao);
      }
    } catch (error) {
      console.error("Erro ao carregar dados:", error);
      toast.error("Erro ao carregar dados do plantão");
    } finally {
      setLoading(false);
    }
  };

  const toggleCorretor = (corretorId) => {
    setSelectedCorretores(prev => 
      prev.includes(corretorId)
        ? prev.filter(id => id !== corretorId)
        : [...prev, corretorId]
    );
  };

  const handleSave = async () => {
    if (selectedCorretores.length === 0) {
      toast.error("Selecione pelo menos um corretor para a roleta");
      return;
    }

    try {
      setSaving(true);
      
      // Calcular datas
      const datasPlantao = [hoje.toISOString().split('T')[0]];
      
      // Se for sexta, incluir sábado e domingo
      if (diaSemana === 5) {
        const sabado = new Date(hoje);
        sabado.setDate(sabado.getDate() + 1);
        const domingo = new Date(hoje);
        domingo.setDate(domingo.getDate() + 2);
        datasPlantao.push(sabado.toISOString().split('T')[0]);
        datasPlantao.push(domingo.toISOString().split('T')[0]);
      }

      await axios.post(`${BACKEND_URL}/api/plantao/configurar`, {
        corretores_roleta: selectedCorretores,
        corretor_plantao: corretorPlantao,
        datas: datasPlantao
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success(
        diaSemana === 5 
          ? "Plantão configurado para sexta, sábado e domingo!" 
          : "Plantão do dia configurado com sucesso!"
      );
      onClose();
    } catch (error) {
      console.error("Erro ao salvar plantão:", error);
      toast.error("Erro ao configurar plantão");
    } finally {
      setSaving(false);
    }
  };

  const formatDataPlantao = () => {
    const options = { weekday: 'long', day: 'numeric', month: 'long' };
    if (diaSemana === 5) {
      return "Sexta, Sábado e Domingo";
    }
    return hoje.toLocaleDateString('pt-BR', options);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[100] p-4">
      <div className="bg-white dark:bg-slate-900 rounded-2xl p-6 w-full max-w-xl shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
              <Users className="w-6 h-6 text-primary" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-slate-800 dark:text-white">
                Configurar Plantão
              </h2>
              <p className="text-sm text-slate-500 dark:text-slate-400 flex items-center gap-1">
                <Calendar className="w-4 h-4" />
                {formatDataPlantao()}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
          >
            <X className="w-5 h-5 text-slate-500" />
          </button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full"></div>
          </div>
        ) : (
          <>
            {/* Alerta para sexta-feira */}
            {diaSemana === 5 && (
              <div className="mb-4 p-3 rounded-xl bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 flex items-start gap-2">
                <AlertCircle className="w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-amber-800 dark:text-amber-200">
                  <strong>Atenção:</strong> Hoje é sexta-feira. A configuração será aplicada para o fim de semana inteiro (sexta, sábado e domingo).
                </p>
              </div>
            )}

            {/* Seção: Roleta de Corretores */}
            <div className="mb-6">
              <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-3 flex items-center gap-2">
                <Clock className="w-4 h-4" />
                Corretores na Roleta (sequencial)
              </h3>
              <p className="text-xs text-slate-500 dark:text-slate-400 mb-3">
                Selecione os corretores que receberão leads automaticamente hoje
              </p>
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {corretores.map(corretor => (
                  <button
                    key={corretor.id}
                    onClick={() => toggleCorretor(corretor.id)}
                    className={`w-full p-3 rounded-xl border-2 transition-all flex items-center justify-between ${
                      selectedCorretores.includes(corretor.id)
                        ? 'border-primary bg-primary/5'
                        : 'border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                        selectedCorretores.includes(corretor.id)
                          ? 'bg-primary text-white'
                          : 'bg-slate-100 dark:bg-slate-800 text-slate-500'
                      }`}>
                        {corretor.name?.charAt(0).toUpperCase() || 'C'}
                      </div>
                      <div className="text-left">
                        <p className="font-medium text-slate-800 dark:text-white text-sm">{corretor.name}</p>
                        <p className="text-xs text-slate-500">{corretor.whatsapp || corretor.phone || 'Sem telefone'}</p>
                      </div>
                    </div>
                    {selectedCorretores.includes(corretor.id) && (
                      <Check className="w-5 h-5 text-primary" />
                    )}
                  </button>
                ))}
              </div>
              {selectedCorretores.length > 0 && (
                <p className="text-xs text-slate-500 mt-2">
                  {selectedCorretores.length} corretor(es) selecionado(s)
                </p>
              )}
            </div>

            {/* Seção: Corretor de Plantão */}
            <div className="mb-6">
              <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-3 flex items-center gap-2">
                <Users className="w-4 h-4" />
                Corretor de Plantão (fixo)
              </h3>
              <p className="text-xs text-slate-500 dark:text-slate-400 mb-3">
                Este corretor receberá todos os contatos via WhatsApp
              </p>
              <select
                value={corretorPlantao || ''}
                onChange={(e) => setCorretorPlantao(e.target.value || null)}
                className="w-full p-3 rounded-xl border-2 border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-800 dark:text-white focus:border-primary outline-none"
              >
                <option value="">Usar roleta (sem plantão fixo)</option>
                {corretores.map(corretor => (
                  <option key={corretor.id} value={corretor.id}>
                    {corretor.name} {corretor.whatsapp ? `(${corretor.whatsapp})` : ''}
                  </option>
                ))}
              </select>
            </div>

            {/* Botões */}
            <div className="flex gap-3">
              <button
                onClick={onClose}
                className="flex-1 px-4 py-3 rounded-xl border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={handleSave}
                disabled={saving || selectedCorretores.length === 0}
                className="flex-1 px-4 py-3 rounded-xl bg-primary text-white font-medium hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {saving ? (
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : (
                  <>
                    <Check className="w-5 h-5" />
                    Confirmar Plantão
                  </>
                )}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default PlantaoModal;
